import React, { useState, useRef, useEffect, useContext } from 'react'
import { screenText, extractCheckbox } from '../content/instructions'
import { taskContent } from '../content/tasks'
import RichText from '../components/RichText'
import StudyIDE from '../components/StudyIDE'
import Modal from '../components/Modal'
import useServerTimer from '../components/useServerTimer'
import { StudyTelemetryProvider, StudyTelemetryContext } from '../telemetry/StudyTelemetryContext'
import { SESSION_EVENTS } from '../telemetry/telemetryEvents'

// Coding task (learning or transfer) — the embedded Cosmos (/chi) IDE, full screen.
// Cosmo rail item ON for learning tasks, absent for transfer. 15-min server-anchored box.
//
// The telemetry provider is remounted per step (keyed on stepId), which resets the
// event buffer and re-emits SESSION_START — one telemetry session per coding task,
// matching the workspace's one-session-per-task shape.
export default function TaskStep (props) {
  return (
    <StudyTelemetryProvider key={props.step.stepId} participantId={props.participantId} stepId={props.step.stepId}>
      <TaskStepBody {...props} />
    </StudyTelemetryProvider>
  )
}

function TaskStepBody ({ step, participantId, onAdvance, busy }) {
  const task = step.task ? taskContent(step.task) : null
  const [code, setCode] = useState(task ? task.starter : '')
  const codeRef = useRef(code)
  useEffect(() => { codeRef.current = code }, [code])

  const { logTelemetryEvent } = useContext(StudyTelemetryContext)
  const logRef = useRef(logTelemetryEvent)
  useEffect(() => { logRef.current = logTelemetryEvent }, [logTelemetryEvent])

  const screen = step.screen ? screenText(step.screen) : ''
  const boxLabel = extractCheckbox(screen) || (step.pledge ? 'I understand this task is to be completed without AI or outside help.' : null)
  const [pledged, setPledged] = useState(false)
  const [started, setStarted] = useState(!step.pledge)

  const remaining = useServerTimer({
    participantId, stepId: step.stepId, timedSec: step.timedSec,
    stepStartedAt: step.stepStartedAt, remainingSec: step.remainingSec,
    onExpire: () => {
      // Logged before onAdvance so it lands in the buffer the provider flushes on unmount.
      // test_results is always [] here: the study runner returns stdout/stderr only and
      // never a grader verdict, by design (studyController.run).
      logRef.current(SESSION_EVENTS.TASK_TIMEOUT, {
        code: codeRef.current,
        test_results: [],
        timestamp: Date.now()
      })
      onAdvance({ code: codeRef.current, reason: 'timeout' }, 'timeout')
    }
  })

  if (!task) return <div className="study-card">No task content for {String(step.task)}.</div>

  const kind = step.type === 'screening_task' ? 'Warm-up Exercise'
    : step.type === 'transfer' ? 'Transfer Task'
      : `Learning Task ${step.ordinal || ''}`

  // The workspace fires TASK_COMPLETED on an all-tests-pass detection. There is no
  // pass signal here, so the participant's own SUBMIT is the completion event.
  const onSubmit = () => {
    logTelemetryEvent(SESSION_EVENTS.TASK_COMPLETED, {
      code,
      timestamp: Date.now()
    })
    onAdvance({ code, reason: 'complete' })
  }

  return (
    <>
      <StudyIDE
        task={task}
        aiAvailable={step.aiAvailable}
        taskNumber={step.questionNumber}
        taskTotal={step.taskTotal}
        participantId={participantId}
        code={code}
        setCode={setCode}
        remaining={remaining}
        onProceed={onSubmit}
        proceedBusy={busy}
        proceedLabel="SUBMIT"
        kind={kind}
        phase={step.phase}
      />
      {step.pledge && !started && (
        <Modal size="md">
          <div className="study-card">
            {screen && <div className="study-intro"><RichText text={screen} /></div>}
            {boxLabel && (
              <label className="study-check">
                <input type="checkbox" checked={pledged} onChange={e => setPledged(e.target.checked)} />
                <span>{boxLabel}</span>
              </label>
            )}
            <div className="study-actions">
              <button className="study-btn study-btn--primary" disabled={!pledged || busy} onClick={() => setStarted(true)}>Begin task</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
